import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

interface ScheduleItem {
  startTime: string;
  endTime: string;
  title: string;
  type: 'ceremony' | 'session' | 'break' | 'meal' | 'social';
}

const day1Schedule: ScheduleItem[] = [
  { startTime: '09:00', endTime: '09:30', title: 'Açılış Töreni', type: 'ceremony' },
  { startTime: '10:00', endTime: '11:20', title: '1. Oturum', type: 'session' },
  { startTime: '11:20', endTime: '11:50', title: 'Kahve Molası', type: 'break' },
  { startTime: '11:50', endTime: '13:10', title: '2. Oturum', type: 'session' },
  { startTime: '13:10', endTime: '14:10', title: 'Öğle Yemeği', type: 'meal' },
  { startTime: '14:10', endTime: '15:30', title: '3. Oturum', type: 'session' },
  { startTime: '15:30', endTime: '16:00', title: 'Kahve Molası', type: 'break' },
  { startTime: '16:00', endTime: '17:20', title: '4. Oturum', type: 'session' },
];

const day2Schedule: ScheduleItem[] = [
  { startTime: '09:30', endTime: '10:50', title: '5. Oturum', type: 'session' },
  { startTime: '10:50', endTime: '11:20', title: 'Kahve Molası', type: 'break' },
  { startTime: '11:20', endTime: '12:40', title: '6. Oturum', type: 'session' },
  { startTime: '12:40', endTime: '13:40', title: 'Öğle Yemeği', type: 'meal' },
  { startTime: '13:40', endTime: '15:10', title: '7. Oturum', type: 'session' },
  { startTime: '15:10', endTime: '15:40', title: 'Kahve Molası', type: 'break' },
  { startTime: '15:40', endTime: '17:00', title: '8. Oturum', type: 'session' },
  { startTime: '17:00', endTime: '17:30', title: 'Kapanış Töreni', type: 'ceremony' },
  { startTime: '17:30', endTime: '18:30', title: 'Sosyal Etkinlik', type: 'social' },
];

const typeConfig: Record<ScheduleItem['type'], { bg: string; border: string; text: string; icon: JSX.Element }> = {
  ceremony: {
    bg: 'bg-amber-50 dark:bg-amber-900/30',
    border: 'border-amber-400',
    text: 'text-amber-700 dark:text-amber-300',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
  },
  session: {
    bg: 'bg-blue-50 dark:bg-blue-900/30',
    border: 'border-blue-400',
    text: 'text-blue-700 dark:text-blue-300',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  break: {
    bg: 'bg-emerald-50 dark:bg-emerald-900/30',
    border: 'border-emerald-400',
    text: 'text-emerald-700 dark:text-emerald-300',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
      </svg>
    ),
  },
  meal: {
    bg: 'bg-orange-50 dark:bg-orange-900/30',
    border: 'border-orange-400',
    text: 'text-orange-700 dark:text-orange-300',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8.1 13.34l2.83-2.83L3.91 3.5a4.008 4.008 0 000 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
      </svg>
    ),
  },
  social: {
    bg: 'bg-purple-50 dark:bg-purple-900/30',
    border: 'border-purple-400',
    text: 'text-purple-700 dark:text-purple-300',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
};

export function CalendarPage() {
  const [selectedDay, setSelectedDay] = useState<1 | 2>(1);

  useEffect(() => {
    document.title = 'Etkinlik Programı | ESDFL MUN';
  }, []);

  const schedule = selectedDay === 1 ? day1Schedule : day2Schedule;
  const dayDate = selectedDay === 1 ? '6 Aralık 2025, Cumartesi' : '7 Aralık 2025, Pazar';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm transition-colors duration-300">
      <Helmet>
        <title>Etkinlik Programı | ESDFL MUN</title>
        <meta name="description" content="ESDFL MUN etkinlik programı - 6-7 Aralık 2025 konferans takvimi" />
      </Helmet>

      <h1 className="text-2xl md:text-3xl font-serif font-bold mb-2 text-gray-900 dark:text-white">Etkinlik Programı</h1>
      <p className="text-gray-600 dark:text-gray-400 mb-6">6-7 Aralık 2025 | Edirne Süleyman Demirel Fen Lisesi</p>

      {/* Day Selector */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => setSelectedDay(1)}
          className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
            selectedDay === 1
              ? 'btn'
              : 'btn-secondary dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          1. Gün - 6 Aralık
        </button>
        <button
          onClick={() => setSelectedDay(2)}
          className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
            selectedDay === 2
              ? 'btn'
              : 'btn-secondary dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          2. Gün - 7 Aralık
        </button>
      </div>

      {/* Selected Day Header */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 p-4 mb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{selectedDay}. Gün</h2>
        <p className="text-gray-600 dark:text-gray-400 text-sm">{dayDate}</p>
      </div>

      {/* Schedule Timeline */}
      <div className="space-y-3">
        {schedule.map((item, index) => {
          const config = typeConfig[item.type];
          return (
            <div
              key={index}
              className={`flex items-center gap-4 p-4 rounded-xl border-l-4 ${config.bg} ${config.border} transition-all hover:shadow-md`}
            >
              {/* Time Display - Stylized vertical layout */}
              <div className="flex-shrink-0 w-16 sm:w-20">
                <div className="flex flex-col items-center">
                  {/* Start Time */}
                  <span className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white tabular-nums tracking-tight">
                    {item.startTime}
                  </span>
                  
                  {/* Decorative dash */}
                  <div className="flex items-center justify-center my-1">
                    <div className={`w-6 h-0.5 rounded-full ${config.border.replace('border-', 'bg-')}`} />
                  </div>
                  
                  {/* End Time */}
                  <span className="text-base sm:text-lg font-semibold text-gray-500 dark:text-gray-400 tabular-nums tracking-tight">
                    {item.endTime}
                  </span>
                </div>
              </div>

              {/* Vertical Divider */}
              <div className={`w-0.5 h-14 rounded-full ${config.border.replace('border-', 'bg-')} opacity-40`} />
              
              {/* Icon */}
              <div className={`flex-shrink-0 w-11 h-11 rounded-xl ${config.bg} ${config.text} flex items-center justify-center border-2 ${config.border} shadow-sm`}>
                {config.icon}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className={`font-bold text-lg sm:text-xl ${config.text}`}>
                  {item.title}
                </h3>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
